// Libs
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import qs from 'qs';

// Config
import { getConfig } from '@app/config';
import { handleAuthError } from '@app/slice/authRedirect';

// baseQuery độc lập hoàn toàn với apiSlice: tự dựng theo từng request để đọc
// runtime config (đổi API URL không cần rebuild) và gắn Bearer token của Owner.
const ownerBaseQuery = async (args, api, extraOptions) => {
  const config = getConfig();

  if (!config) {
    return { error: { status: 500, data: 'Config can not loaded yet' } };
  }

  const rawBaseQuery = fetchBaseQuery({
    baseUrl: config.REACT_APP_OWNER_API_URL || '',
    prepareHeaders: (headers) => {
      // Token owner nạp qua localStorage 'ownerToken' (phát bởi POST /auth/owner-login,
      // mang claim HotelID → backend chọn đúng DB). Login chưa có token thì bỏ qua.
      const token = localStorage.getItem('ownerToken');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
    // Vỏ bọc ApiResponse<T> = { success, data, message, errors, pagination }.
    // owner-login trả TRẦN (không có success) → body?.success !== false vẫn true.
    validateStatus: (response, body) =>
      response.status === 200 && body?.success !== false,
    paramsSerializer: (params) => qs.stringify(params),
  });

  const result = await rawBaseQuery(args, api, extraOptions);
  // 401 ở API bất kỳ (trừ owner-login) → xoá token, về màn login.
  return handleAuthError(result, args);
};

export const ownerApiSlice = createApi({
  reducerPath: 'ownerApi',
  baseQuery: ownerBaseQuery,
  tagTypes: ['OwnerDashboard', 'RoomOps'],
  endpoints: (builder) => ({
    // Đăng nhập chủ đầu tư (PA4) — POST /api/v1/auth/owner-login body {username,password}.
    // Response TRẦN (KHÔNG bọc ApiResponse): { token, expiresAt, displayName, hotelId }.
    // Sai → 401 (body ApiResponse có message); sai 5 lần → khoá 15' (message chứa giờ mở khoá).
    ownerLogin: builder.mutation({
      query: (body) => ({
        url: '/auth/owner-login',
        method: 'POST',
        body,
      }),
      transformResponse: (resp) => resp,
    }),
    // Tự đổi mật khẩu (đã đăng nhập) — POST /api/v1/auth/owner-change-password
    // body { currentPassword, newPassword }. Danh tính lấy từ claim của token, KHÔNG gửi username.
    // Response ApiResponse<string>; lỗi nghiệp vụ trả 400 (sai mật khẩu cũ / mật khẩu mới không
    // đạt / trùng mật khẩu cũ) — CỐ Ý không dùng 401 để không đá người dùng về màn login.
    ownerChangePassword: builder.mutation({
      query: (body) => ({
        url: '/auth/owner-change-password',
        method: 'POST',
        body,
      }),
      transformResponse: (resp) => resp?.data ?? resp,
    }),
    getOwnerDashboard: builder.query({
      // horizon backend hỗ trợ 14|30|60|90 — MẶC ĐỊNH 30 (đổi 20/08/2026, tester
      // round 5 bug 2008: khớp số liệu hiển thị ban đầu). Chỉ tác dụng lên khối dự báo/dòng tiền.
      query: ({ horizon = 30 } = {}) => ({
        url: '/owner-dashboard',
        params: { horizon },
      }),
      // Bóc vỏ ApiResponse, trả thẳng DTO. finance nằm TRONG DTO (data.finance) —
      // KHÔNG có endpoint /finance riêng. finance=null ⇒ chưa có số kế toán (không phải lỗi).
      transformResponse: (resp) => resp?.data ?? resp,
      providesTags: ['OwnerDashboard'],
    }),
    // Room-ops (CI/CO + trạng thái phòng) — GET /room-ops/summary.
    // Trả về: arrival*/departure* (booking ≠ phòng), inHouse/dirty/ready/clean/ooo/vp,
    // extraBeds, asOfDate. Nguồn vận hành DiCloud (Housekeeping/FrontOffice).
    getRoomOpsSummary: builder.query({
      query: () => ({ url: '/room-ops/summary' }),
      transformResponse: (resp) => resp?.data ?? resp,
      providesTags: ['RoomOps'],
      // Giữ 10' (mặc định 60s): asOfDate của response này là MỎ NEO khoảng ngày cho
      // mọi màn báo cáo (ReportShell) — hết cache là màn báo cáo lại phải chờ neo.
      keepUnusedDataFor: 600,
    }),
  }),
});

export const {
  useOwnerLoginMutation,
  useOwnerChangePasswordMutation,
  useGetOwnerDashboardQuery,
  useGetRoomOpsSummaryQuery,
} = ownerApiSlice;
